import React from 'react';
import PropTypes from 'prop-types';

import { get } from 'lodash';
import { reduxForm, Field } from 'redux-form';
import { CANCEL_BOOKING_FORM } from '../../app-main/components/reduxForms/constants/formNames';
import validateCancelBookingForm from './validate/validateCancelBookingForm';
import submitCancelBookingForm from './submit/submitCancelBookingForm';
import { showFormError, verifyPSCredits } from '../../app-bookings-request/forms/bookingUtil';

import SelectField from '../../shared-components/reduxForms/fields/SelectField/SelectField';
import TextAreaField from '../../shared-components/reduxForms/fields/TextAreaField/TextAreaField';
import Button from '../../elements-buttons/Button';
import RequiredLabel from '../../elements-inputs/RequiredLabel';
import { TosCTAdisclaimer } from 'shared-components/terms/cta-disclaimer';
import CreditIncentiveBanner from '../credit-incentive-banner';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';

const CancelBookingForm = ({
  cancelReasonValue,
  booking,
  bookingSummaryDisplay,
  displayError,
  backAction,
  creditIncentiveAction,
  handleSubmit,
  isProcessing,
  paymentDetails,
  flowStep,
  cancelContractObj,
  intl
}) => {
  const isOtherSelected = 'Other' === cancelReasonValue;
  const hostName = get(booking, 'booking.host.name.first', 'the host');

  const creditRefundAmount = get(paymentDetails, 'credit_amount', 0);
  const creditRefundSelected = 'creditRefund' === flowStep;
  const standardRefundSelected = 'standardRefund' === flowStep;

  const listing = get(booking, 'booking.listing', {});
  const psCreditsAllowed = verifyPSCredits(listing);
  const incentiveEligible = get(cancelContractObj, 'incentive_credit_eligible', false);
  const displayIncentiveBanner = standardRefundSelected && incentiveEligible && psCreditsAllowed;

  const cancelOptions = [
    { value: 'My event is cancelled',
      label: intl.formatMessage({ id: 'inbox_id_cancel_booking.cancel_booking_form.event_canceled', defaultMessage: 'My event is cancelled' }) },
    { value: 'I found another space',
      label: intl.formatMessage({ id: 'inbox_id_cancel_booking.cancel_booking_form.found_space', defaultMessage: 'I found another space' }) },
    { value: 'I booked the incorrect listing',
      label: intl.formatMessage({ id: 'inbox_id_cancel_booking.cancel_booking_form.incorrect_listing', defaultMessage: 'I booked the incorrect listing' }) },
    { value: 'The space is no longer a fit for my event',
      label: intl.formatMessage({ id: 'inbox_id_cancel_booking.cancel_booking_form.no_fit', defaultMessage: 'The space is no longer a fit for my event' }) },
    { value: 'Other',
      label: intl.formatMessage({ id: 'inbox_id_cancel_booking.cancel_booking_form.other', defaultMessage: 'Other' }) }
  ];

  return (
    <form onSubmit={handleSubmit}>
      <div className="cancel-booking-form-container">
        <div className="heavy margin-sm-bottom">
          <FormattedMessage
            id='inbox_id_cancel_booking.cancel_booking_form.why_cancelling'
            defaultMessage='Tell Peerspace why you are cancelling'
          /> <span className="text-teal-300">*</span>
        </div>

        <div className="flex column">
          <Field
            component={SelectField}
            name="cancelReason"
            className="form-fields"
            placeholder={ intl.formatMessage({ id: 'inbox_id_cancel_booking.cancel_booking_form.select_reason', defaultMessage: 'Select a reason' }) }
            options={cancelOptions}
            maxMenuHeight={260}
            attributes={{ 'data-testing-id': 'reason-field' }}
          />

          { isOtherSelected &&
            <Field
              className="margin-xl-top"
              component={TextAreaField}
              name="otherReason"
              rows={5}
              attributes={{ 'data-testing-id': 'other-reason-field' }}
            /> }

          <hr className="full-width" />
          <div className="heavy margin-sm-bottom">
            <FormattedMessage
              id='inbox_id_cancel_booking.cancel_booking_form.write_message'
              defaultMessage='Write a message to {name}'
              values={{
                name: hostName
              }}
            /> <span className="text-teal-300">*</span>
          </div>

          <Field
            component={TextAreaField}
            name="messageToHost"
            placeholder={ intl.formatMessage({ id: 'inbox_id_cancel_booking.cancel_booking_form.need_cancel', defaultMessage: 'Unfortunately I need to cancel my event' }) }
            rows={5}
            attributes={{ 'data-testing-id': 'message-to-host' }}
          />

          <hr className="margin-sm-bottom full-width" />

          { bookingSummaryDisplay() }

          { displayIncentiveBanner && <CreditIncentiveBanner creditIncentiveAction={creditIncentiveAction} /> }

          <div className="help-block text-danger text-left margin-xl-top">{ displayError }</div>

          <div className="cta-buttons flex space-between column-xs-reverse">
            <Button
              className="classic full-height"
              category="gray-outline"
              type="button"
              attributes={{ 'data-testing-id': 'cancel-booking-back-btn' }}
              onClick={backAction}
            >
              <FormattedMessage
                id='inbox_id_cancel_booking.cancel_booking_form.back'
                defaultMessage='Back'
              />
            </Button>

            <Button
              className="margin-md-bottom"
              type="submit"
              attributes={{ 'data-testing-id': 'cancel-booking-submit-btn' }}
              category="primary"
              isLoading={isProcessing}
              disabled={isProcessing}
            >
              <FormattedMessage
                id='inbox_id_cancel_booking.cancel_booking_form.cancel_booking'
                defaultMessage='Cancel booking'
              />
            </Button>
          </div>

          <hr className="margin-lg-bottom full-width" />
          <TosCTAdisclaimer
            location={(creditRefundAmount || creditRefundSelected) ? 'cancelBkgCredits' : 'cancelBkg'}
            buttonLabel={ intl.formatMessage({ id: 'inbox_id_cancel_booking.cancel_booking_form.cancel_booking_label', defaultMessage: 'Cancel booking' }) }
          />

        </div>
      </div>
    </form>
  );
};

CancelBookingForm.propTypes = {
  cancelReasonValue: PropTypes.string,
  booking: PropTypes.object,
  bookingSummaryDisplay: PropTypes.func,
  displayError: PropTypes.string,
  backAction: PropTypes.func,
  handleSubmit: PropTypes.func,
  isProcessing: PropTypes.bool,
  intl: intlShape
};

const config = {
  form: CANCEL_BOOKING_FORM,
  onSubmit: submitCancelBookingForm,
  validate: validateCancelBookingForm,
  onSubmitFail: showFormError
};

export default injectIntl(reduxForm(config)(CancelBookingForm));
